import type { LocalText } from './types';

/**
 * The menu as a display cabinet (ตู้โชว์): every category is a shelf, its dishes stand on plates in a row and wrap
 * onto more shelves when there are many. Pure layout + drag-and-drop maths; the 3D scene draws what this says.
 */
export interface ShowcaseDish { id: string; categoryId: string; name: LocalText; priceSatang: number; sticker?: string; soldOut: boolean }
export interface ShowcaseCategory { id: string; name: LocalText; dishes: ShowcaseDish[] }

export const SLOT_W = 0.9;
export const ROW_H = 1.2;
/** Extra space above each category's first shelf, for its name. */
export const HEADER = 0.55;

export interface Slot { categoryId: string; index: number; row: number; col: number; x: number; y: number; dishId?: string; add?: boolean }
export interface ShowcaseLayout {
  cols: number;
  slots: Slot[];
  /** First row of each category (its label sits at the left end of that shelf). */
  shelves: { categoryId: string; row: number; y: number; first: boolean }[];
  rows: number;
  width: number;
  height: number;
}

/** Columns that fit a stage width (CSS px): 3 on phones, up to 6 on wide screens. */
export const colsFor = (px: number) => Math.max(3, Math.min(6, Math.floor(px / 200)));

/**
 * Lay the cabinet out: categories in order, dishes left to right, wrapping after `cols`. With `addSlots`, each
 * category ends with an empty "+" plate (the owner adds a dish there).
 */
export function layoutShowcase(categories: ShowcaseCategory[], cols: number, addSlots = false): ShowcaseLayout {
  const slots: Slot[] = [];
  const shelves: ShowcaseLayout['shelves'] = [];
  let row = 0;
  const x = (col: number) => (col - (cols - 1) / 2) * SLOT_W;
  categories.forEach((c, ci) => {
    const y = (rr: number) => -(rr * ROW_H + ci * HEADER);
    const n = c.dishes.length + (addSlots ? 1 : 0);
    const rowsHere = Math.max(1, Math.ceil(n / cols));
    for (let r = 0; r < rowsHere; r++) shelves.push({ categoryId: c.id, row: row + r, y: y(row + r), first: r === 0 });
    c.dishes.forEach((d, i) => {
      const col = i % cols, rr = row + Math.floor(i / cols);
      slots.push({ categoryId: c.id, index: i, row: rr, col, x: x(col), y: y(rr), dishId: d.id });
    });
    if (addSlots) {
      const i = c.dishes.length, col = i % cols, rr = row + Math.floor(i / cols);
      slots.push({ categoryId: c.id, index: i, row: rr, col, x: x(col), y: y(rr), add: true });
    }
    row += rowsHere;
  });
  return { cols, slots, shelves, rows: row, width: cols * SLOT_W, height: row * ROW_H + Math.max(0, categories.length - 1) * HEADER };
}

/**
 * Where a dragged dish would land for a point on the cabinet front (scene units): the category of the shelf under
 * the point and the position in that category, clamped to the category's end.
 */
export function dropTarget(layout: ShowcaseLayout, categories: ShowcaseCategory[], x: number, y: number): { categoryId: string; index: number } | null {
  if (!layout.shelves.length) return null;
  const shelf = layout.shelves.reduce((best, s) => (Math.abs(s.y - y) < Math.abs(best.y - y) ? s : best));
  const row = shelf.row;
  const firstRow = layout.shelves.find((s) => s.categoryId === shelf.categoryId && s.first)!.row;
  const col = Math.max(0, Math.min(layout.cols - 1, Math.round(x / SLOT_W + (layout.cols - 1) / 2)));
  const cat = categories.find((c) => c.id === shelf.categoryId)!;
  return { categoryId: cat.id, index: Math.min(cat.dishes.length, (row - firstRow) * layout.cols + col) };
}

/** The cabinet after moving one dish to (categoryId, index). Nothing else changes order. */
export function moveDish(categories: ShowcaseCategory[], dishId: string, categoryId: string, index: number): ShowcaseCategory[] {
  const dish = categories.flatMap((c) => c.dishes).find((d) => d.id === dishId);
  if (!dish) return categories;
  const without = categories.map((c) => ({ ...c, dishes: c.dishes.filter((d) => d.id !== dishId) }));
  return without.map((c) => {
    if (c.id !== categoryId) return c;
    const dishes = [...c.dishes];
    dishes.splice(Math.max(0, Math.min(index, dishes.length)), 0, { ...dish, categoryId });
    return { ...c, dishes };
  });
}

/** What to send to PUT /menu/arrange: each dish whose category or position changed, with sort = position (1-based). */
export function arrangeBody(before: ShowcaseCategory[], after: ShowcaseCategory[]) {
  const was = new Map(before.flatMap((c) => c.dishes.map((d, i) => [d.id, `${c.id}:${i}`] as const)));
  return {
    items: after.flatMap((c) => c.dishes.flatMap((d, i) => (was.get(d.id) === `${c.id}:${i}` ? [] : [{ id: d.id, categoryId: c.id, sort: i + 1 }]))),
  };
}
